import CAIcon from '@ds/components/shared/CAIcon'
import type {
	CAButtonSize,
	CAButtonStyle,
	CAButtonType,
} from '@ds/interfaces'
import { defineComponent, type PropType } from 'vue'

import CAButton from '../buttons/CAButton'

/**
	* The CADropdown component
	* @component CADropdown
	*
	* Types for the CADropdown component
	* @typedef {('compact' | 'large'| 'medium' | 'small')} ButtonSize - The size of the button.
	* @typedef {('dark' | 'filled-light' | 'filled-dark' | 'outlined' | 'text')} ButtonStyle - The style of the button.
	*
	* @prop {string} buttonClass - The class for the dropdown button
	* @prop {string} buttonDataTest - The data-test attribute for the button
	* @prop {boolean} buttonDisabled - Whether the button is disabled (dropdown won't open)
	* @prop {boolean} buttonGroup - Whether the button is part of a button group (ignore if this isn't in a CAButtonGroup)
	* @prop {string} buttonId - The id for the button
	* @prop {ButtonSize} buttonSize - The size of the button
	* @prop {string} buttonStyle - The style of the button
	* @prop {ButtonType} buttonType - The type of the button
	* @prop {boolean} dark - Whether the dropdown is dark
	* @prop {string} dropdownDataTest - The data-test attribute for the dropdown
	* @prop {boolean} hideArrow - Whether the arrow should be hidden
	* @prop {string} menuClass - The class
	* @prop {boolean} simple - Whether the dropdown is simple or contains text instead
	* @prop {boolean} showLeft = Whether the dropdown should open to the left
	* @prop {boolean} sustain - Whether the dropdown should stay open after clicking outside of it. Leave it false if you want it to close after clicking outside of it.
	*
	* @slot button - The button slot - use this for the button title (not available with simple dropdown)
	* @slot default - The default is used for the menu items
	* @slot header - The slot for the header in the dropdown
	*
	* @emit click - Emits when the dropdown button is clicked
	* @emit close - Emits when the dropdown is closed
	*
*/

export default defineComponent({
	name: 'CADropdown',

	props: {
		/**
		 * The class for the dropdown button
		 * @default ''
		 * @type {string}
		 */
		buttonClass: { default: '', type: String },
		/**
		 * The data-test attribute for the button
		 * @default ''
		 * @type {string}
		 */
		buttonDataTest: { default: '', type: String },
		/**
		 * Whether the button is disabled (dropdown won't open)
		 * @default false
		 * @type {boolean}
		 */
		buttonDisabled: { default: false, type: Boolean },
		/**
		 * Whether the button is part of a button group (ignore if this isn't in a CAButtonGroup)
		 * @default false
		 * @type {boolean}
		 */
		buttonGroup: { default: false, type: Boolean },
		/**
		 * The id for the button
		 * @default ''
		 * @type {string}
		 */
		buttonId: { default: '', type: String },
		/**
		 * The size of the button
		 * @default 'medium'
		 * @type {ButtonSize}
		 * @enum {'compact' | 'full' | 'large' | 'medium' | 'small'}
		 */
		buttonSize: { default: 'medium', type: String as PropType<CAButtonSize> },
		/**
		 * The style of the button
		 * @default 'filled-light'
		 * @type {ButtonStyle}
		 * @enum {'dark' | 'filled-light' | 'filled-dark' | 'outlined' | 'text'}
		 */
		buttonStyle: { default: 'filled-light', type: String as PropType<CAButtonStyle> },
		/**
		 * The title for the button
		 * @default ''
		 * @type {string}
		 */
		buttonTitle: { default: '', type: String },
		/**
		 * The type of the button
		 * @default 'secondary'
		 * @type {ButtonType}
		 * @enum {'danger' | 'group' | 'primary-alt' | 'primary' | 'secondary' | 'slideover' | 'success' | 'warning'}
		 */
		buttonType: { default: 'secondary', type: String as PropType<CAButtonType> },
		/**
		 * Whether the dropdown is dark
		 * @default false
		 * @type {boolean}
		 */
		dark: { default: false, type: Boolean },
		/**
		 * The data-test attribute for the dropdown
		 * @default 'ca-dropdown-menu'
		 * @type {string}
		 */
		dropdownDataTest: { default: 'ca-dropdown-menu', type: String },
		/**
		 * Whether the arrow should be hidden
		 * @default false
		 * @type {boolean}
		 */
		hideArrow: { default: false, type: Boolean },
		/**
		 * The class for the menu
		 * @default ''
		 * @type {string}
		 */
		menuClass: { default: '', type: String },
		/**
		 * Whether the dropdown should open to the left
		 * @default false
		 * @type {boolean}
		 */
		showLeft: { default: false, type: Boolean },
		/**
		 * Whether the dropdown is a simple dropdown
		 * @default false
		 * @type {boolean}
		 */
		simple: { default: false, type: Boolean },
		/**
		 * Whether the dropdown should stay open after clicking outside of it
		 * @default false
		 * @type {boolean}
		 */
		sustain: { default: false, type: Boolean },
	},

	emits: {
		click: (_event?: Event) => true,
		close: () => true,
	},

	data(): Data {
		return { dropdownOpen: false }
	},

	computed: {
		buttonClasses(): string {
			return `ca-dropdown__button
			${this.buttonClass}
			${this.dropdownOpen ? 'ca-dropdown__button-open' : ''}
			${this.dark ? 'ca-dropdown__button-dark text-gray-50 bg-gray-800 hover:bg-gray-700' : 'ca-dropdown__button-light'}`
		},

		menuClasses(): string {
			return `ca-dropdown__menu
			${this.showLeft ? 'ca-dropdown__menu--left' : ''}
			${this.menuClass}
			${this.dropdownOpen ? 'ca-dropdown__open' : ''}
			${this.dark ? 'ca-dropdown__menu-dark' : 'ca-dropdown__menu-light'}`
		},
	},

	watch: {
		dropdownOpen(isOpen: boolean) {
			if (!isOpen) {
				this.$emit('close')
			}
		},
	},

	methods: {
		handleClick() {
			this.dropdownOpen = !this.dropdownOpen
		},

		handleEvent() {
			this.dropdownOpen = false
		},
	},

	render() {
		const button =
			<CAButton
				aria-controls={`${this.buttonId}_menu`}
				aria-expanded={this.dropdownOpen.toString()}
				aria-haspopup="true"
				button-group={this.buttonGroup}
				buttonStyle={this.buttonStyle}
				class={this.buttonClasses}
				data-test={this.buttonDataTest}
				disabled={this.buttonDisabled}
				size={this.buttonSize}
				type={this.buttonType}
				onClick={this.handleClick}
			>
				{!this.simple && this.$slots.button ?
					this.$slots.button?.()

					:

					<>
						{this.buttonTitle ?
							<span>{this.buttonTitle}</span>

							:

							<span class="sr-only">Toggle dropdown</span>
						}
					</>
				}

				{this.simple &&
					<CAIcon
						aria-hidden="true"
						icon="SVGSimpleDropdown"
						svg-class="ca-dropdown-simple"
					/>
				}

				{!this.simple && !this.hideArrow && <span aria-hidden="true" class="ca-dropdown-chevron" />}
			</CAButton>

		const menu =
			<div
				aria-labelledby={this.buttonId}
				aria-orientation="vertical"
				class={this.menuClasses}
				data-test={this.dropdownDataTest}
				id={`${this.buttonId}_menu`}
				role="menu"
				onMouseleave={this.handleEvent}
			>
				{this.$slots.header?.()}
				{this.$slots.default?.()}
			</div>

		return <div
			class="ca-dropdown"
			data-test="ca-dropdown"
			v-click-outside={this.handleEvent}
			onClick={() => this.$emit('click')}
		>
			{button}
			{menu}
		</div>
	},
})

interface Data {
	dropdownOpen: boolean
}
